/**
 * Use shop route state.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { useCallback, useEffect, useState } from 'react'
import { parseRoute } from '../routing'
import type { AppProps, Route } from '../types'
import { DEFAULT_SHOP_LOCALE, inferLocaleFromPathname, withLocalePrefix } from '../../i18n/shopLocale'

/**
 * Use shop route state.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const useShopRouteState = ({
  initialPathname = '/',
  initialSearch = '',
  initialLocale = DEFAULT_SHOP_LOCALE,
}: AppProps) => {
  const [route, setRoute] = useState<Route>(() => {
    if (typeof window === 'undefined') {
      return parseRoute(initialPathname, initialSearch)
    }
    return parseRoute(window.location.pathname, window.location.search)
  })

  const navigateTo = useCallback((targetPath: string) => {
    const localeFromPath = inferLocaleFromPathname(window.location.pathname)
    const activeLocale = localeFromPath ?? initialLocale
    window.history.pushState({}, '', withLocalePrefix(targetPath, activeLocale))
    setRoute(parseRoute(window.location.pathname, window.location.search))
  }, [initialLocale])

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname, window.location.search))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  return {
    route,
    navigateTo,
  }
}
